'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DolgununErimeSuresiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dolgunun-erime-suresi-ne-zaman-ve-neden-azalir';
  const pageTitle = 'Dolgunun Erime Süresi: Ne Zaman ve Neden Azalır?';
  const pageDescription = 'Dolgu ne kadar sürede erir? Hyaluronik asit dolguların kalıcılığını etkileyen faktörler nelerdir ve etkisini uzatmak için neler yapılabilir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dolgunun Erime Süresi', href: '/tr/blog/dolgunun-erime-suresi-ne-zaman-ve-neden-azalir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dolgunun erime süresi ve kalıcılığını etkileyen faktörler"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dolgu Bakımı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dolgunun Erime Süresi: Ne Zaman ve Neden Azalır?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dolgu uygulamaları yüz gençleştirme ve hacim kazandırmada en çok tercih edilen estetik işlemlerden biridir. Ancak her dolgu zamanla vücut tarafından parçalanır ve etkisini yavaşça kaybeder.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Peki dolgunun erime süresi neye bağlıdır ve bu süre nasıl uzatılabilir?
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dolgunun Erime Süresini Belirleyen Faktörler
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Faktör
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etkisi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dolgu türü</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit (HA), CaHA, PLLA</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6 ay – 3 yıl</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Uygulama bölgesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Dudak, burun, yanak, çene</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hareketli bölgelerde daha hızlı erir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Metabolizma hızı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüksek metabolizma dolguyu hızlı parçalar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Süreyi kısaltır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kullanılan dolgu yoğunluğu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yoğun ürünler daha kalıcıdır</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kalıcılığı uzatır</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Cilt yapısı ve yaşı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">İnce ciltte dolgu daha hızlı emilir</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Süreyi azaltır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Mimik ve kas hareketi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Sürekli mimik kullanılan alanlarda erime artar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Dudak gibi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Dolgu Türlerine Göre Ortalama Kalıcılık
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu Türü
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Kalıcılık Süresi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Hyaluronik Asit (HA)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">8–18 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalsiyum Hidroksiapatit (CaHA)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–24 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Poli-L-Laktik Asit (PLLA)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">18–36 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yağ Transferi (Lipofilling)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 yıl</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">PMMA (Kalıcı Dolgu)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">5+ yıl (nadiren tercih edilir)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Dolgunun Erimesi Normal Bir Süreçtir
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Vücut, hyaluronik asidi doğal yollarla parçalayarak metabolize eder. Bu nedenle dolgunun erimesi vücudun doğal yenilenme mekanizmasının bir parçasıdır.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Erime genellikle eşit ve simetrik olur, ancak hızlı metabolizmaya sahip kişilerde daha erken fark edilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Dolgu Etkisini Uzatmanın Yolları
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Bol su içmek</li>
                  <li>✅ Aşırı sıcak (sauna, solaryum) ortamlarından kaçınmak</li>
                  <li>✅ Güneş koruyucu kullanmak</li>
                  <li>✅ Sigara ve alkol tüketimini azaltmak</li>
                  <li>✅ Cilt bakım rutini oluşturmak</li>
                  <li>✅ Gerektiğinde "touch-up" seansları yaptırmak</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  💡 Dolguyu korumanın sırrı, vücudu desteklemektir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Dolgu Erimesi Hızlandığında Ne Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu hızlı eridiyse şu seçenekler değerlendirilebilir:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Daha yoğun dolgu tipi seçmek</li>
                  <li>Aynı bölgeye ikinci katman uygulaması yapmak</li>
                  <li>Kombine teknik (dolgu + PRP) kullanmak</li>
                  <li>Hyaluronidaz enzimiyle kontrollü düzeltme yapmak</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Dolgu Eridikten Sonra Tekrar Uygulama
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu tamamen eridikten sonra aynı bölgeye yeniden uygulama yapılabilir. Aksine, bu durumda doku hafızası nedeniyle dolgu daha doğal oturur.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 font-medium">
                  Uygulamalar arası ideal süre: 8–12 ay.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Dolgunun erime süresi ve kalıcılığını etkileyen faktörler"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Dolgum neden hızlı eridi?</h3>
                  <p className="text-gray-700">Yüksek metabolizma, spor, sıcak ortamlar ve sık mimik kullanımı erimeyi hızlandırır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Dudak dolgusu neden daha çabuk kayboluyor?</h3>
                  <p className="text-gray-700">Dudak, hareketli bir bölge olduğu için HA daha hızlı parçalanır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu tamamen eriyince yüzüm sarkar mı?</h3>
                  <p className="text-gray-700">Hayır. Dolgu eridiğinde cilt eski haline döner, deformasyon olmaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Erimemesi için ne yapabilirim?</h3>
                  <p className="text-gray-700">Bol su içmek, güneşten korunmak ve düzenli bakım en etkili yöntemlerdir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Dolgu erimeden yenilenebilir mi?</h3>
                  <p className="text-gray-700">Evet, kalan dolgu üzerine yeni katman uygulanabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Aesthetic Plastic Surgery (ASAPS) – "Longevity of Dermal Fillers"</li>
                <li>Harvard Health Publishing (2024) – "Why Fillers Fade: Understanding Metabolism"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Kalıcılığı ve Erime Süresi Çalışması"</li>
                <li>ISAPS Global Report 2024 – "Dermal Filler Duration Data"</li>
                <li>Mayo Clinic – "Care Tips for Maintaining Fillers"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#kalıcılık</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetikbakım</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#yüzgençleştirme</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#prp</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Dolgu Bakımı Hakkında Bilgi Alın</h3>
              <p className="mb-6">Dolgu kalıcılığını uzatmak ve doğru bakım yöntemleri hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Dolgu erime süresi ve bakımı hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu işlemlerinin yüz liftingi etkisi yaratıp yaratmadığı ve nasıl uygulandığı.</p>
            </Link>
            <Link
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe işlemi, hangi içerikler kullanılır ve nasıl uygulanır?</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
